import { OrgMemberDetail } from "@garther/interfaces";
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuDivider,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";

import { setCachedOrgId } from "@/features/org/hooks/use-navigate-to-org";

interface OrgSwitcherProps {
  className?: string;
  currentOrgId: string;
  orgs: OrgMemberDetail[];
}

export function OrgSwitcher({ className, currentOrgId, orgs }: OrgSwitcherProps) {
  const navigate = useNavigate();
  const { t } = useI18n();

  const curOrg = orgs.find((m) => m.org.id === currentOrgId || m.org.slug === currentOrgId);
  const currentOrg = curOrg?.org;

  const handleSelect = (orgId: string) => {
    setCachedOrgId(orgId);
    navigate({ to: "/org/$orgId", params: { orgId } });
  };

  const handleCreateOrg = () => {
    navigate({ to: "/org/create" });
  };

  return (
    <DropdownMenu className={className} closeAfterClick>
      <DropdownMenuTrigger className="w-full">
        <div className="flex items-center gap-2 pr-1 py-2 rounded hover:bg-background-highlight transition-colors w-full">
          <Avatar
            name={currentOrg?.name ?? ""}
            src={currentOrg?.avatar}
            size={9}
            className="flex-shrink-0 ml-1"
          />
          <span className="flex-1 hidden sm:block text-responsive-md font-medium text-text-primary truncate text-left">
            {currentOrg?.name ?? t("orgSwitcher.selectOrg")}
          </span>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="left" width="w-fit min-w-[200px]">
        <DropdownMenuLabel>{t("orgSwitcher.myOrgs")}</DropdownMenuLabel>

        <DropdownMenuDivider />
        {orgs.map((member) => {
          const isSelected = member.org.id === currentOrg?.id;
          return (
            <DropdownMenuItem
              key={member.org.id}
              onClick={() => handleSelect(member.org.slug || member.org.id)}
              className={cn(isSelected ? "bg-background-highlight" : "")}
            >
              <div className={cn("flex items-center gap-2 w-full min-w-0 max-w-[300px]")}>
                <Avatar name={member.org.name} src={member.org.avatar} size="sm" className="flex-shrink-0" />
                <span className="truncate text-sm">{member.org.name}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuDivider />
        <DropdownMenuItem icon={<Plus className="w-3.5 h-3.5" />} onClick={handleCreateOrg}>
          {t("orgSwitcher.createOrg")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
